import React, { useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { useUIStore } from '../../stores/uiStore';

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { currentTheme, setTheme } = useUIStore();

  // Detect and apply system theme preference on initial load if no preference is stored
  useEffect(() => {
    const storedData = localStorage.getItem('lecturo-ui-store');

    // If no stored preference exists, use system preference
    if (!storedData) {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setTheme(prefersDark ? 'dark' : 'light');
    }
  }, [setTheme]); // Only run once on mount

  // Set data-theme attribute on document root for CSS variable switching
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', currentTheme);
  }, [currentTheme]);

  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode: currentTheme,
          primary: {
            main: '#FF9A76',
            light: '#FFB399',
            dark: '#E6876B',
          },
          secondary: {
            main: '#A04CFF',
            light: currentTheme === 'light' ? '#EDDDFF' : '#4A1A6B',
            dark: currentTheme === 'light' ? '#C996FF' : '#7B3FCF',
          },
          background: {
            default: currentTheme === 'light' ? '#f8f8f8' : '#121212',
            paper: currentTheme === 'light' ? '#ffffff' : '#2d2d2d',
          },
        },
        typography: {
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          h1: {
            fontWeight: 300,
          },
          h2: {
            fontWeight: 400,
          },
          h3: {
            fontWeight: 400,
          },
          h4: {
            fontWeight: 500,
          },
          h5: {
            fontWeight: 500,
          },
          h6: {
            fontWeight: 500,
          },
        },
        shape: {
          borderRadius: 8,
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                borderRadius: 8,
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: 12,
              },
            },
          },
          MuiTextField: {
            defaultProps: {
              variant: 'outlined',
            },
          },
          MuiTableCell: {
            styleOverrides: {
              root: {
                ...(currentTheme === 'dark' && {
                  borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                }),
              },
            },
          },
        },
      }),
    [currentTheme]
  );

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  );
};
